import { PDFPageProxy } from 'pdfjs-dist'

import {
  AnnotationMetadata,
  EAnnotationEngine,
  HTMLAnchorFragment,
  PDFAnchorFragment,
  PdfPageProxyLike,
} from '@/features/annotation'

export type PdfAnnotation = AnnotationMetadata & {
  engine: EAnnotationEngine.PDF
  fragments: PDFAnchorFragment[]
}

export type HtmlAnnotation = AnnotationMetadata & {
  engine: EAnnotationEngine.HTML
  fragments: HTMLAnchorFragment[]
}

export function asPdfAnnotation(
  metadata: AnnotationMetadata | HTMLAnchorFragment | null | undefined
): PdfAnnotation | null {
  if (
    !metadata ||
    !('engine' in metadata) ||
    metadata.engine !== EAnnotationEngine.PDF ||
    !Array.isArray(metadata.fragments)
  ) {
    return null
  }

  return metadata as PdfAnnotation
}

export function asHtmlAnnotation(
  metadata: AnnotationMetadata | null | undefined
): HtmlAnnotation | null {
  if (!metadata) return null
  if (!Array.isArray(metadata.fragments) || metadata.fragments.length === 0)
    return null

  // If engine exists, it must be HTML
  if (
    'engine' in metadata &&
    metadata.engine &&
    metadata.engine !== EAnnotationEngine.HTML
  ) {
    return null
  }

  const first = metadata.fragments[0] as any
  const isHtmlFragment =
    first &&
    typeof first.startPath === 'string' &&
    typeof first.endPath === 'string' &&
    typeof first.startOffset === 'number' &&
    typeof first.endOffset === 'number'

  if (!isHtmlFragment) return null

  // If engine missing, you can normalize it to HTML for internal use (optional)
  return {
    ...(metadata as any),
    engine: EAnnotationEngine.HTML,
  } as HtmlAnnotation
}

// Minimal adapter: converts pdf.js viewport "any[]" into strict [number, number]
export const toTuple2 = (v: unknown): [number, number] => {
  if (
    Array.isArray(v) &&
    typeof v[0] === 'number' &&
    typeof v[1] === 'number'
  ) {
    return [v[0], v[1]]
  }
  return [0, 0]
}

export const asPdfPageProxyLike = (page: PDFPageProxy): PdfPageProxyLike => ({
  getTextContent: () => page.getTextContent(),
  getViewport: (params: { scale: number }) => {
    const vp = page.getViewport(params)
    return {
      width: vp.width,
      height: vp.height,
      convertToViewportPoint: (x: number, y: number) =>
        toTuple2(vp.convertToViewportPoint(x, y)),
      convertToPdfPoint: (x: number, y: number) =>
        toTuple2(vp.convertToPdfPoint(x, y)),
    }
  },
})
